import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Crown, TrendingUp, AlertCircle, AlertTriangle, Check, RefreshCw, Calendar, CreditCard, Zap, Sparkles, ArrowRight, Users, MapPin, MessageSquare, Phone, PhoneOff } from 'lucide-react';
import { SubscriptionService } from '../../api/services/SubscriptionService';
import { CompanySettingsService } from '../../api/services/CompanySettingsService';
import { ApiError } from '../../api/core/ApiError';
import { billingManager } from '../../business/billing/BillingManager';
import { config } from '../../config/config';
import { useNotifications } from '../../contexts/NotificationContext';
import type { Routemize_DTOs_Billing_TenantSubscriptionDto } from '../../api/models/Routemize_DTOs_Billing_TenantSubscriptionDto';
import { Routemize_Constants_SubscriptionTier } from '../../api/models/Routemize_Constants_SubscriptionTier';
import type { Routemize_DTOs_Billing_DowngradeBlockerDto } from '../../api/models/Routemize_DTOs_Billing_DowngradeBlockerDto';
import DowngradeBlockerResolver from './DowngradeBlockerResolver';
import { getApiErrorMessage } from '../../utils/apiErrorMessage';

/**
 * Custom Area (zip-code territories) is a Pro+ feature. When a tenant downgrades to
 * Solo we have to drop them back to a simple radius service area. 40 miles is the
 * agreed default radius applied on this forced switch.
 */
const SOLO_DEFAULT_RADIUS_MILES = 40;

/**
 * True when the tenant's saved service area is Custom Area (zip-code territories)
 * rather than a simple radius. `serviceAreaMode` is `'zipcode'` for Custom Area.
 */
const hasCustomAreaServiceArea = (serviceAreaMode?: string | null): boolean => {
  return serviceAreaMode === 'zipcode';
};

// Declare Chargebee global types
declare global {
  interface Window {
    Chargebee?: any;
  }
}

interface ManageSubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubscriptionUpdated?: (updated?: Routemize_DTOs_Billing_TenantSubscriptionDto) => void;
  /** Called after a scheduled cancellation is undone (keepSubscription). Falls back to onSubscriptionUpdated. */
  onSubscriptionKept?: (updated: Routemize_DTOs_Billing_TenantSubscriptionDto) => void;
  initialTab?: 'overview' | 'change';
}

type SubscriptionTier = 'Solo' | 'Pro' | 'Scale';
type BillingPeriod = 'Monthly' | 'Annual';

interface TierInfo {
  name: SubscriptionTier;
  tagline: string;
  monthlyPrice: number;
  annualPrice: number;
  features: (string | { text: string; badge?: 'PRO' | 'SOON' })[];
  maxAppointments: number;
  maxEventTypes: number;
  maxUsers: number;
  gradient: string;
  icon: React.ReactNode;
}

const TIER_INFO: Record<SubscriptionTier, TierInfo> = {
  Solo: {
    name: 'Starter',
    tagline: 'For small teams',
    monthlyPrice: 49,
    annualPrice: 39,
    features: [
      'Unlimited bookings',
      '1 user + 1 Team Member included',
      '2 booking forms',
      'Google Calendar',
      'SMS/Email reminders',
      'Radius territory only',
    ],
    maxAppointments: -1,
    maxEventTypes: 2,
    maxUsers: 1,
    gradient: 'from-amber-500 to-orange-600',
    icon: <Zap className="w-5 h-5" />,
  },
  Pro: {
    name: 'Pro',
    tagline: 'For teams',
    monthlyPrice: 99,
    annualPrice: 79,
    features: [
      'Unlimited bookings',
      '2 users included',
      '5 booking forms',
      'Chatbot',
      { text: 'Zapier integration' },
      'Smart Fill, Add a Stop, Plan a Stop',
      'Smart Assignment',
      { text: 'Custom Territories', badge: 'PRO' },
      { text: 'Form Pre-qualification', badge: 'PRO' },
      { text: 'AI Enhance Route', badge: 'PRO' },
    ],
    maxAppointments: -1,
    maxEventTypes: 5,
    maxUsers: 2,
    gradient: 'from-blue-500 to-blue-600',
    icon: <Sparkles className="w-5 h-5" />,
  },
  Scale: {
    name: 'Scale',
    tagline: 'For growing operations',
    monthlyPrice: 199,
    annualPrice: 159,
    features: [
      'Everything in Pro',
      '4 users included',
      'Unlimited booking forms',
      'Voice Agent',
      'Priority chat support',
      'Setup session included',
    ],
    maxAppointments: -1,
    maxEventTypes: -1,
    maxUsers: 4,
    gradient: 'from-emerald-500 to-blue-600',
    icon: <Crown className="w-5 h-5" />,
  }
};

const ManageSubscriptionModal: React.FC<ManageSubscriptionModalProps> = ({
  isOpen,
  onClose,
  onSubscriptionUpdated,
  onSubscriptionKept,
  initialTab = 'overview'
}) => {
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState<Routemize_DTOs_Billing_TenantSubscriptionDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'change'>(initialTab);
  const [selectedTier, setSelectedTier] = useState<SubscriptionTier>('Pro');
  const [selectedPeriod, setSelectedPeriod] = useState<BillingPeriod>('Monthly');

  // Chargebee integration state
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [chargebeeLoaded, setChargebeeLoaded] = useState(false);
  const chargebeeInstanceRef = useRef<any>(null);
  const cardComponentRef = useRef<any>(null);

  // Payment method state
  const [hasPaymentMethods, setHasPaymentMethods] = useState(false);
  const [primaryPaymentMethod, setPrimaryPaymentMethod] = useState<any>(null);

  // Scale downgrade warning: shown when a Scale tenant with active phone numbers
  // downgrades to a lower tier. The phone number(s) will be released at end-of-term.
  const [showScaleDowngradeWarning, setShowScaleDowngradeWarning] = useState(false);
  const [activeVoiceLineCount, setActiveVoiceLineCount] = useState(0);

  // Solo downgrade warning: shown when a Pro/Scale tenant on Custom Area (zip-code
  // territories) downgrades to Solo. We force them back to a 40-mile radius service
  // area because Custom Area is a Pro+ feature, and they also lose the Chatbot.
  const [showSoloDowngradeWarning, setShowSoloDowngradeWarning] = useState(false);
  const [companySettingsId, setCompanySettingsId] = useState<string | null>(null);

  // Downgrade blockers: when the target tier's limits are exceeded, we show an inline resolver
  // (archive forms / add seats / remove members) instead of a dead-end error.
  const [downgradeBlockers, setDowngradeBlockers] = useState<Routemize_DTOs_Billing_DowngradeBlockerDto[] | null>(null);

  useEffect(() => {
    if (isOpen) {
      setActiveTab(initialTab);
      setShowScaleDowngradeWarning(false);
      setActiveVoiceLineCount(0);
      setShowSoloDowngradeWarning(false);
      setCompanySettingsId(null);
      setDowngradeBlockers(null);
      loadSubscription();
      loadChargebeeScript();
    }

    return () => {
      // Cleanup card component when modal closes
      if (cardComponentRef.current) {
        try {
          cardComponentRef.current.unmount();
        } catch (e) {
          // Ignore unmount errors
        }
        cardComponentRef.current = null;
      }
    };
  }, [isOpen]);

  const loadChargebeeScript = () => {
    // Check if already loaded
    if (window.Chargebee) {
      setChargebeeLoaded(true);
      return;
    }

    // Load Chargebee script
    const script = document.createElement('script');
    script.src = 'https://js.chargebee.com/v2/chargebee.js';
    script.async = true;
    script.onload = () => {
      setChargebeeLoaded(true);
    };
    script.onerror = () => {
      console.error('Failed to load Chargebee script');
      setError('Failed to load payment form');
    };
    document.body.appendChild(script);
  };

  const initializeChargebee = async () => {
    if (!window.Chargebee || chargebeeInstanceRef.current) return;

    try {
      chargebeeInstanceRef.current = window.Chargebee.init({
        site: config.chargebee.site,
        publishableKey: config.chargebee.publishableKey
      });

      await chargebeeInstanceRef.current.load('components');
      console.log('Chargebee initialized successfully');
    } catch (err) {
      console.error('Failed to initialize Chargebee:', err);
      setError('Failed to initialize payment form');
    }
  };

  const initializeCardComponent = () => {
    if (!chargebeeInstanceRef.current || cardComponentRef.current) return;

    try {
      cardComponentRef.current = chargebeeInstanceRef.current.createComponent('card', {
        style: {
          base: {
            fontSize: '16px',
            fontFamily: 'Poppins, sans-serif',
            color: '#1F2937',
            '::placeholder': {
              color: '#9CA3AF'
            }
          },
          invalid: {
            color: '#EF4444'
          }
        }
      });

      const cardContainer = document.getElementById('chargebee-card-element');
      if (cardContainer) {
        cardComponentRef.current.mount(cardContainer);

        cardComponentRef.current.on('ready', () => {
          console.log('Chargebee card component ready');
        });

        cardComponentRef.current.on('change', (event: any) => {
          const errorElement = document.getElementById('chargebee-card-errors');
          if (errorElement) {
            errorElement.textContent = event.error ? event.error.message : '';
          }
        });
      }
    } catch (err) {
      console.error('Failed to initialize card component:', err);
      setError('Failed to initialize payment form');
    }
  };

  useEffect(() => {
    if (showPaymentForm && chargebeeLoaded) {
      initializeChargebee().then(() => {
        initializeCardComponent();
      });
    }
  }, [showPaymentForm, chargebeeLoaded]);

  // Returns the freshly loaded DTO so callers can hand the authoritative state straight to
  // onSubscriptionUpdated instead of racing the setSubscription state update.
  const loadSubscription = async (): Promise<Routemize_DTOs_Billing_TenantSubscriptionDto | undefined> => {
    try {
      setLoading(true);
      setError(null);

      // Load subscription and payment methods in parallel
      const [data, paymentMethods] = await Promise.all([
        SubscriptionService.getApiAppSubscriptionGetCurrentSubscription(),
        billingManager.getPaymentMethods().catch(() => [])
      ]);

      setSubscription(data);

      if (data.currentTier) {
        setSelectedTier(data.currentTier as SubscriptionTier);
      }
      if (data.billingPeriod) {
        setSelectedPeriod(data.billingPeriod as BillingPeriod);
      }

      // Set payment method info
      setHasPaymentMethods(paymentMethods.length > 0);
      setPrimaryPaymentMethod(paymentMethods.length > 0 ? paymentMethods[0] : null);

      return data;
    } catch (err) {
      console.error('Failed to load subscription:', err);
      if (err instanceof ApiError && (err.status === 404 || err.body?.error?.code === 'Subscription:NotFound')) {
        setError('No subscription found. Please create a subscription first.');
      } else {
        setError('Failed to load subscription details');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancelScheduledChange = async () => {
    try {
      setLoading(true);
      setError(null);

      await billingManager.cancelScheduledChange();

      const refreshed = await loadSubscription();
      onSubscriptionUpdated?.(refreshed);
    } catch (err) {
      console.error('Failed to cancel scheduled plan change:', err);
      const message = err instanceof ApiError
        ? (err.body?.error?.message as string | undefined)
        : undefined;
      setError(message || 'Failed to cancel the scheduled plan change');
    } finally {
      setLoading(false);
    }
  };

  const handleChangeTier = async () => {
    if (!subscription) return;

    if (selectedTier === subscription.currentTier && selectedPeriod === subscription.billingPeriod) {
      // With a pending downgrade, re-selecting the plan you are on is the natural "keep my current
      // plan" action — honour it instead of erroring (ClickUp 86bbqtb71).
      if (subscription.scheduledTier) {
        await handleCancelScheduledChange();
        return;
      }
      setError('Please select a different plan or billing period');
      return;
    }

    // A subscription scheduled to cancel at end of term (non_renewing) cannot be changed in Chargebee.
    // Pre-empt the round-trip and steer the user to the Overview tab's "Keep my subscription" button to
    // reactivate first, rather than dead-ending on the backend Subscription:ScheduledForCancellation
    // error (ClickUp 86bcbukya).
    if (subscription.cancelAtPeriodEnd) {
      setError(
        'Your subscription is scheduled to cancel. Undo the cancellation first ("Keep my subscription" ' +
        'on the Overview tab, or "Undo cancellation" on the Billing page), then change your plan.'
      );
      setActiveTab('overview');
      return;
    }

    // Pre-flight validation for downgrades only
    const tierOrder = { Solo: 0, Pro: 1, Scale: 2 };
    const currentTierRank = tierOrder[subscription.currentTier as keyof typeof tierOrder] ?? 0;
    const selectedTierRank = tierOrder[selectedTier as keyof typeof tierOrder] ?? 0;
    const isDowngrade = selectedTierRank < currentTierRank;

    if (isDowngrade) {
      try {
        setLoading(true);
        setError(null);
        const validation = await billingManager.validateTierChange(selectedTier as Routemize_Constants_SubscriptionTier);
        if (!validation.canChange) {
          // Show the inline resolver (archive forms / add seats / remove members) instead of a
          // dead-end error — the user fixes each blocker and re-checks without leaving the modal.
          setDowngradeBlockers(validation.blockers ?? []);
          setLoading(false);
          return;
        }

        // Warn if leaving Scale with active voice agent phone numbers — they will be
        // released at the end of the current billing period.
        const voiceLines = validation.activeVoiceLines ?? 0;
        if (subscription.currentTier === 'Scale' && selectedTier !== 'Scale' && voiceLines > 0) {
          setActiveVoiceLineCount(voiceLines);
          setShowScaleDowngradeWarning(true);
          setLoading(false);
          return;
        }
      } catch (err: any) {
        console.error('Pre-flight validation failed:', err);
        setError(err?.message || 'Failed to validate plan change');
        setLoading(false);
        return;
      }
    }

    // Downgrading to Solo drops Pro+ features. If the tenant is on Custom Area
    // (zip-code territories) we must warn them — they lose their saved zip codes
    // and the Chatbot, and we force their service area back to a 40-mile radius.
    if (isDowngrade && selectedTier === 'Solo') {
      try {
        setLoading(true);
        setError(null);
        const settings = await CompanySettingsService.getApiAppCompanySettingsGet();
        if (hasCustomAreaServiceArea(settings.serviceAreaMode)) {
          setCompanySettingsId(settings.id ?? null);
          setShowSoloDowngradeWarning(true);
          setLoading(false);
          return;
        }
      } catch (err: any) {
        console.error('Failed to load company settings before downgrade:', err);
        setError(err?.message || 'Failed to validate plan change');
        setLoading(false);
        return;
      }
    }

    // If we already know there is no card on file, skip the failed round-trip and the "pushed toward
    // Scale" confusion — go straight to the add-a-card form (ClickUp 86bbufna1). hasPaymentMethodOnFile
    // is sourced live from Chargebee on the subscription DTO; null means "unknown", so only act on an
    // explicit false. The end-of-term Scale-downgrade path returns above (via the warning) before
    // reaching here, so this only affects immediate changes that bill now.
    if (subscription.hasPaymentMethodOnFile === false) {
      setShowPaymentForm(true);
      return;
    }

    // Try to change tier without payment method first
    // Backend will determine if payment method is needed
    await proceedWithTierChange();
  };

  /**
   * User confirmed the Scale downgrade warning (active voice/phone numbers will be
   * released at period end). If they are also downgrading to Solo AND are on Custom
   * Area, show that warning next; otherwise proceed immediately.
   */
  const confirmScaleDowngrade = async () => {
    setShowScaleDowngradeWarning(false);

    if (selectedTier === 'Solo') {
      try {
        setLoading(true);
        setError(null);
        const settings = await CompanySettingsService.getApiAppCompanySettingsGet();
        if (hasCustomAreaServiceArea(settings.serviceAreaMode)) {
          setCompanySettingsId(settings.id ?? null);
          setShowSoloDowngradeWarning(true);
          setLoading(false);
          return;
        }
      } catch (err: any) {
        console.error('Failed to load company settings before downgrade:', err);
        setError(err?.message || 'Failed to validate plan change');
        setLoading(false);
        return;
      }
    }

    await proceedWithTierChange();
  };

  /**
   * User confirmed the Solo downgrade from the Custom Area warning. Proceed with the
   * tier change and reset their service area to a 40-mile radius afterwards.
   */
  const confirmSoloDowngrade = async () => {
    // Keep the warning mounted while processing so its button shows the loading state.
    // It's cleared when the modal closes on success (or reset on next open).
    await proceedWithTierChange(undefined, true);
  };

  /**
   * Force the tenant's service area back to a simple radius. Custom Area (zip-code
   * territories) is a Pro+ feature, so a Solo plan can't keep saved zip codes.
   * Sent as a partial update — only the service-area fields are touched, all other
   * company settings are preserved by the backend (same pattern as RoutePreferencesPanel).
   */
  const resetServiceAreaToRadius = async () => {
    if (!companySettingsId) return;
    try {
      await CompanySettingsService.putApiAppCompanySettingsUpdate(companySettingsId, {
        serviceAreaMode: 'radius',
        serviceAreaZipCodes: null,
        maxTravelRadiusMiles: SOLO_DEFAULT_RADIUS_MILES,
      });
    } catch (err) {
      // Don't block the (already successful) downgrade if this cleanup fails — log it
      // so it can be retried/observed. The user can re-save radius from settings.
      console.error('Failed to reset service area to radius after Solo downgrade:', err);
    }
  };

  const proceedWithTierChange = async (paymentMethodId?: string, resetServiceArea = false) => {
    if (!subscription) return;

    try {
      setLoading(true);
      setError(null);

      const updated = await SubscriptionService.postApiAppSubscriptionChangeSubscriptionTier({
        tier: selectedTier,
        billingPeriod: selectedPeriod,
        paymentMethodId: paymentMethodId
      });

      // Custom Area is a Pro+ feature — drop Solo tenants back to a radius service area.
      if (resetServiceArea) {
        await resetServiceAreaToRadius();
      }

      // Success! Close the modal and reload subscription. Pass the authoritative DTO up so the
      // dashboard can distinguish an applied change from one scheduled for end-of-term.
      await loadSubscription();
      onSubscriptionUpdated?.(updated);
      setShowPaymentForm(false);
      onClose();
    } catch (err: any) {
      console.error('Failed to change subscription:', err);

      // Check if error is due to missing payment method
      const errorCode = err.body?.error?.code || '';
      const errorMessage = err.body?.error?.message || err.body?.error?.data?.Message || '';
      // Backend signals "add a card first" with the Subscription:PaymentMethodRequired code (not a 403,
      // and not a guaranteed message substring). Fall back to the old heuristics only as a safety net.
      const isPaymentMethodRequired =
        errorCode === 'Subscription:PaymentMethodRequired'
        || err.status === 403
        || errorMessage.toLowerCase().includes('payment method');

      if (err.body?.error?.code === 'Subscription:ScheduledForCancellation') {
        // Subscription is scheduled to cancel (non_renewing). Steer the user to Keep Subscription
        // first — the "Keep my subscription" button lives on the Overview tab — then let them change
        // the plan (ClickUp 86bcbukya).
        setError(
          'Your subscription is scheduled to cancel. Undo the cancellation first ("Keep my subscription" ' +
          'on the Overview tab, or "Undo cancellation" on the Billing page), then change your plan.'
        );
        setActiveTab('overview');
        setLoading(false);
      } else if (err.body?.error?.code === 'Subscription:ScheduledChangePending') {
        // A pending end-of-term change blocks a different one. Point at the way out rather than
        // showing the generic "please try again or contact support".
        setError(
          `A change to the ${subscription.scheduledTier} plan is already scheduled` +
          (subscription.scheduledChangeEffectiveDate
            ? ` for ${billingManager.formatDate(subscription.scheduledChangeEffectiveDate)}`
            : '') +
          `. Select ${subscription.currentTier} to keep your current plan, then choose a different one.`
        );
        setLoading(false);
      } else if (isPaymentMethodRequired && !paymentMethodId) {
        // Payment method is required - show payment form
        console.log('Payment method required - showing payment form');
        setShowPaymentForm(true);
        setLoading(false);
      } else {
        // Other error - display error message
        setError(errorMessage || 'Failed to change subscription. Please try again or contact support.');
        setLoading(false);
      }
    }
  };

  // Undo a scheduled cancellation (Chargebee non_renewing): no charge, current term preserved. This
  // is the safe path for a "cancelling" (cancel-at-period-end) subscription — it calls Chargebee's
  // remove_scheduled_cancellation, so it never re-bills a fresh term or overwrites the prepaid
  // period. A genuinely Cancelled subscription is reactivated (and re-billed) from the Billing
  // dashboard, which states the immediate charge before confirming.
  const handleKeep = async () => {
    try {
      setLoading(true);
      setError(null);

      const updated = await billingManager.keepSubscription();

      await loadSubscription();
      if (onSubscriptionKept) onSubscriptionKept(updated);
      else onSubscriptionUpdated?.();
      onClose();
    } catch (err: unknown) {
      console.error('Failed to keep subscription:', err);
      setError(getApiErrorMessage(err, 'Failed to keep subscription'));
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentFormSubmit = async () => {
    if (!cardComponentRef.current) return;

    setLoading(true);
    setError(null);

    try {
      console.log('Starting Chargebee tokenization for plan upgrade...');

      // Tokenize the card
      const tokenResult = await cardComponentRef.current.tokenize();

      console.log('Tokenization completed!');
      console.log('Token result:', tokenResult);

      const token = tokenResult?.token;

      if (!token) {
        const structure = JSON.stringify(tokenResult, null, 2);
        console.error('Token not found in result:', structure);
        setError(`Failed to tokenize card. Please check your card details.`);
        setLoading(false);
        return;
      }

      // Proceed with tier change using the Chargebee token as payment method ID
      console.log('Processing plan upgrade with payment token...');
      await proceedWithTierChange(token);

    } catch (err: any) {
      console.error('Error during payment process:', err);
      setError(err?.message || 'Failed to process payment. Please try again.');
      setLoading(false);
    }
  };

  const renderOverview = () => {
    if (!subscription) return null;

    const currentTierInfo = TIER_INFO[subscription.currentTier as SubscriptionTier] || TIER_INFO.Pro;
    const isCancelled = subscription.status === 'Cancelled';
    const willCancelAtPeriodEnd = subscription.cancelAtPeriodEnd;

    return (
      <div className="space-y-6">
        {/* Current Plan */}
        <div className="bg-gradient-to-br from-blue-50 to-blue-50 rounded-lg p-6 border border-blue-200">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Crown className="h-6 w-6 text-blue-600" />
                <h3 className="text-xl font-bold text-ink-900">{subscription.currentTier} Plan</h3>
              </div>
              <p className="text-2xl font-bold text-blue-600">
                ${subscription.billingPeriod === 'Monthly' ? subscription.monthlyPrice : subscription.annualPrice}
                <span className="text-sm font-normal text-ink-600">
                  /{subscription.billingPeriod === 'Monthly' ? 'month' : 'year'}
                </span>
              </p>
              {subscription.billingPeriod === 'Annual' && (
                <p className="text-sm text-green-600 font-medium mt-1">
                  Save ${(subscription.monthlyPrice || 0) * 12 - (subscription.annualPrice || 0)}/year
                </p>
              )}
            </div>
            <div className="text-right">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                isCancelled ? 'bg-red-100 text-red-700' :
                willCancelAtPeriodEnd ? 'bg-orange-100 text-orange-700' :
                'bg-green-100 text-green-700'
              }`}>
                {isCancelled ? 'Cancelled' :
                 willCancelAtPeriodEnd ? 'Cancelling' :
                 subscription.isInTrial ? 'Trial' : 'Active'}
              </span>
            </div>
          </div>
        </div>

        {/* Cancellation Notice */}
        {willCancelAtPeriodEnd && !isCancelled && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-orange-900">Subscription Cancelling</p>
                <p className="text-sm text-orange-700 mt-1">
                  Your subscription will end on {billingManager.formatDate(subscription.currentPeriodEnd || '')}.
                  You'll be downgraded to the Starter plan after this date. Keep your subscription to
                  stay on your current plan through the term you've already paid for — no charge now,
                  and your billing date doesn't change.
                </p>
                {/* eslint-disable-next-line no-restricted-syntax -- ManageSubscriptionModal: tier-selector cards with active-state border tint and brand pricing CTAs */}
                <button
                  onClick={handleKeep}
                  disabled={loading}
                  className="mt-3 px-4 py-2 bg-orange-600 text-white rounded-lg text-sm font-medium hover:bg-orange-700 transition-colors disabled:opacity-50"
                >
                  {loading ? 'Keeping...' : 'Keep my subscription'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Pending End-of-Term Plan Change */}
        {subscription.scheduledTier && !isCancelled && !willCancelAtPeriodEnd && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-amber-900">Plan Change Scheduled</p>
                <p className="text-sm text-amber-700 mt-1">
                  Your plan changes from {subscription.currentTier} to {subscription.scheduledTier}
                  {subscription.scheduledChangeEffectiveDate
                    ? ` on ${billingManager.formatDate(subscription.scheduledChangeEffectiveDate)}`
                    : ' at the end of your current billing period'}.
                  You keep your {subscription.currentTier} plan and its features until then.
                </p>
                {/* eslint-disable-next-line no-restricted-syntax -- ManageSubscriptionModal: tier-selector cards with active-state border tint and brand pricing CTAs */}
                <button
                  onClick={handleCancelScheduledChange}
                  disabled={loading}
                  className="mt-3 px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700 transition-colors disabled:opacity-50"
                >
                  {loading ? 'Cancelling...' : `Keep my ${subscription.currentTier} plan`}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Plan Features */}
        <div>
          <h4 className="font-semibold text-ink-900 mb-3">Current Plan Includes:</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {currentTierInfo.features.map((feature, index) => {
              const f = typeof feature === 'string' ? { text: feature } : feature;
              return (
                <div key={index} className={`flex items-center gap-2 ${f.badge === 'PRO' ? 'bg-violet-50 -mx-1 px-1 py-1 rounded' : f.badge === 'SOON' ? 'text-ink-400' : ''}`}>
                  <Check className={`h-4 w-4 flex-shrink-0 ${f.badge === 'PRO' ? 'text-violet-500' : f.badge === 'SOON' ? 'text-ink-300' : 'text-green-600'}`} />
                  <span className={`text-sm flex-1 ${f.badge === 'PRO' ? 'font-semibold text-ink-900' : 'text-ink-700'}`}>{f.text}</span>
                  {f.badge === 'PRO' && <span className="text-9 font-extrabold tracking-wider px-1.5 py-0.5 rounded bg-gradient-to-r from-violet-600 to-purple-500 text-white">PRO</span>}
                  {f.badge === 'SOON' && <span className="text-9 font-extrabold tracking-wider px-1.5 py-0.5 rounded bg-ink-100 text-ink-500 border border-ink-200">SOON</span>}
                </div>
              );
            })}
          </div>
        </div>

        {/* Billing Information */}
        <div className="border-t border-ink-200 pt-6">
          <h4 className="font-semibold text-ink-900 mb-4">Billing Information</h4>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-ink-600">Next Billing Date</span>
              <span className="text-sm font-medium text-ink-900">
                {subscription.nextBillingDate ? billingManager.formatDate(subscription.nextBillingDate) : 'N/A'}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-ink-600">Auto-Renew</span>
              <span className="text-sm font-medium text-ink-900">
                {subscription.autoRenew ? 'Enabled' : 'Disabled'}
              </span>
            </div>
            {subscription.chargebeeSubscriptionId && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-ink-600">Subscription ID</span>
                <span className="text-sm font-mono text-ink-900 truncate ml-4">
                  {subscription.chargebeeSubscriptionId}
                </span>
              </div>
            )}
          </div>
        </div>


      </div>
    );
  };

  const renderChangePlan = () => {
    const selectedTierInfo = TIER_INFO[selectedTier];
    const price = selectedPeriod === 'Monthly' ? selectedTierInfo.monthlyPrice : selectedTierInfo.annualPrice;

    // Downgrade blockers: the tenant exceeds the target tier's limits. Let them resolve each blocker
    // inline (archive forms / add seats / remove members), then re-run the change once cleared.
    if (downgradeBlockers && downgradeBlockers.length > 0) {
      return (
        <DowngradeBlockerResolver
          targetTier={selectedTier}
          blockers={downgradeBlockers}
          onCancel={() => { setDowngradeBlockers(null); setError(null); }}
          onResolved={() => { setDowngradeBlockers(null); handleChangeTier(); }}
        />
      );
    }

    // Scale downgrade warning: tenant has active Voice Agent phone number(s) that
    // will be released at the end of the current billing period.
    if (showScaleDowngradeWarning) {
      return (
        <div className="space-y-6">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
            <div className="flex items-start gap-3">
              <PhoneOff className="h-6 w-6 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-lg font-bold text-amber-900">Your Voice Agent phone number will be released</h3>
                <p className="text-sm text-amber-800 mt-1">
                  Voice Agent is only available on the Scale plan. If you downgrade, your phone number
                  {activeVoiceLineCount > 1 ? 's' : ''} will remain active until the end of your current billing
                  period, then be permanently released.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-start gap-3 p-4 bg-surface border border-ink-200 rounded-xl">
              <Phone className="h-5 w-5 text-rose-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-ink-900">
                  {activeVoiceLineCount} active phone number{activeVoiceLineCount !== 1 ? 's' : ''} will be released
                </p>
                <p className="text-sm text-ink-600 mt-0.5">
                  At the end of your billing cycle your Twilio phone number{activeVoiceLineCount !== 1 ? 's' : ''} will
                  be released and all Voice Agent routing will stop. This cannot be undone.
                </p>
              </div>
            </div>
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <div className="flex gap-3">
            {/* eslint-disable-next-line no-restricted-syntax -- ManageSubscriptionModal: tier-selector cards with active-state border tint and brand pricing CTAs */}
            <button
              type="button"
              onClick={() => {
                setShowScaleDowngradeWarning(false);
                setError(null);
              }}
              disabled={loading}
              className="flex-1 bg-ink-100 text-ink-700 py-2.5 px-4 rounded-xl font-medium hover:bg-ink-200 transition-colors disabled:opacity-50"
            >
              Keep my current plan
            </button>
            {/* eslint-disable-next-line no-restricted-syntax -- ManageSubscriptionModal: tier-selector cards with active-state border tint and brand pricing CTAs */}
            <button
              type="button"
              onClick={confirmScaleDowngrade}
              disabled={loading}
              className="flex-1 bg-amber-600 text-white py-2.5 px-4 rounded-xl font-medium hover:bg-amber-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Downgrading...
                </>
              ) : (
                'Downgrade anyway'
              )}
            </button>
          </div>
        </div>
      );
    }

    // Solo downgrade warning for tenants on Custom Area (zip-code territories).
    if (showSoloDowngradeWarning) {
      return (
        <div className="space-y-6">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-6 w-6 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-lg font-bold text-amber-900">Switching to Starter changes your account</h3>
                <p className="text-sm text-amber-800 mt-1">
                  Custom Area and the Chatbot are Pro features. If you downgrade to Starter, the following
                  changes will be made to your account.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-start gap-3 p-4 bg-surface border border-ink-200 rounded-xl">
              <MapPin className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-ink-900">Your service area switches to Radius</p>
                <p className="text-sm text-ink-600 mt-0.5">
                  Your Custom Area will be replaced with a {SOLO_DEFAULT_RADIUS_MILES}-mile travel radius in
                  Route Preferences. You can adjust the radius afterwards in Settings.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-4 bg-surface border border-ink-200 rounded-xl">
              <AlertCircle className="h-5 w-5 text-rose-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-ink-900">You'll lose your saved zip codes</p>
                <p className="text-sm text-ink-600 mt-0.5">
                  All zip codes and territories you've configured for your Custom Area will be permanently removed.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-4 bg-surface border border-ink-200 rounded-xl">
              <MessageSquare className="h-5 w-5 text-rose-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-ink-900">You'll lose the Chatbot feature</p>
                <p className="text-sm text-ink-600 mt-0.5">
                  The AI booking chatbot is not available on the Starter plan and will be disabled.
                </p>
              </div>
            </div>
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <div className="flex gap-3">
            {/* eslint-disable-next-line no-restricted-syntax -- ManageSubscriptionModal: tier-selector cards with active-state border tint and brand pricing CTAs */}
            <button
              type="button"
              onClick={() => {
                setShowSoloDowngradeWarning(false);
                setError(null);
              }}
              disabled={loading}
              className="flex-1 bg-ink-100 text-ink-700 py-2.5 px-4 rounded-xl font-medium hover:bg-ink-200 transition-colors disabled:opacity-50"
            >
              Keep my current plan
            </button>
            {/* eslint-disable-next-line no-restricted-syntax -- ManageSubscriptionModal: tier-selector cards with active-state border tint and brand pricing CTAs */}
            <button
              type="button"
              onClick={confirmSoloDowngrade}
              disabled={loading}
              className="flex-1 bg-amber-600 text-white py-2.5 px-4 rounded-xl font-medium hover:bg-amber-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Downgrading...
                </>
              ) : (
                'Downgrade to Starter anyway'
              )}
            </button>
          </div>
        </div>
      );
    }

    // If payment form is showing, render payment form instead of plan selection
    if (showPaymentForm) {
      // Derive the verb from tier order so a Scale->Pro downgrade isn't mislabelled "upgrade".
      const tierOrder = { Solo: 0, Pro: 1, Scale: 2 };
      const currentRank = tierOrder[subscription?.currentTier as keyof typeof tierOrder] ?? 0;
      const selectedRank = tierOrder[selectedTier as keyof typeof tierOrder] ?? 0;
      const changeVerb = selectedRank < currentRank ? 'change to' : 'upgrade to';
      const confirmVerb = selectedRank < currentRank ? 'Confirm Change' : 'Complete Upgrade';
      return (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <CreditCard className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-blue-900">Payment Method Required</p>
                <p className="text-sm text-blue-700 mt-1">
                  Please add a payment method to {changeVerb} {TIER_INFO[selectedTier].name} plan (${price}/{selectedPeriod === 'Monthly' ? 'month' : 'year'}).
                </p>
              </div>
            </div>
          </div>

          <p className="text-sm text-ink-600 mb-4">We accept Visa, Mastercard and American Express</p>

          {/* Error Message */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Chargebee Card Element */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-ink-700 mb-2">
              Card Information
            </label>
            <div id="chargebee-card-element" className="border border-ink-300 rounded-lg p-3 bg-surface"></div>
            <div id="chargebee-card-errors" className="text-red-600 text-sm mt-2"></div>
          </div>

          <p className="text-xs text-ink-500 mb-6">
            Your payment information is securely processed and encrypted. By continuing, you agree to be charged ${price} {selectedPeriod.toLowerCase()} for your {TIER_INFO[selectedTier].name} subscription.
          </p>

          {/* Action Buttons */}
          <div className="flex gap-3">
            {/* eslint-disable-next-line no-restricted-syntax -- ManageSubscriptionModal: tier-selector cards with active-state border tint and brand pricing CTAs */}
            <button
              type="button"
              onClick={() => {
                setShowPaymentForm(false);
                setError(null);
              }}
              className="flex-1 bg-ink-100 text-ink-900 py-2 px-4 rounded-lg font-medium hover:bg-ink-200 transition-colors"
            >
              Back
            </button>
            {/* eslint-disable-next-line no-restricted-syntax -- ManageSubscriptionModal: tier-selector cards with active-state border tint and brand pricing CTAs */}
            <button
              type="button"
              onClick={handlePaymentFormSubmit}
              disabled={loading}
              className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Processing...
                </div>
              ) : (
                `${confirmVerb} - $${price}/${selectedPeriod === 'Monthly' ? 'mo' : 'yr'}`
              )}
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Payment Method Info */}
        {hasPaymentMethods && primaryPaymentMethod && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex gap-3">
              <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h4 className="font-semibold text-green-900 mb-2">Payment Method</h4>
                <div className="flex items-center gap-2 bg-surface rounded px-3 py-2 border border-green-200">
                  <CreditCard className="h-4 w-4 text-green-600" />
                  <span className="font-medium text-sm text-ink-900">
                    {primaryPaymentMethod.cardType || 'Card'} ••••{primaryPaymentMethod.last4}
                  </span>
                  {primaryPaymentMethod.expiryMonth && primaryPaymentMethod.expiryYear && (
                    <span className="text-ink-600 text-sm ml-2">
                      Exp {primaryPaymentMethod.expiryMonth}/{primaryPaymentMethod.expiryYear}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Billing Period Toggle */}
        <div className="flex justify-center">
          <div className="inline-flex items-center bg-ink-100 rounded-full p-1">
            {/* eslint-disable-next-line no-restricted-syntax -- ManageSubscriptionModal: tier-selector cards with active-state border tint and brand pricing CTAs */}
            <button
              onClick={() => setSelectedPeriod('Monthly')}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                selectedPeriod === 'Monthly'
                  ? 'bg-surface text-ink-900 shadow-sm'
                  : 'text-ink-600 hover:text-ink-900'
              }`}
            >
              Monthly
            </button>
            {/* eslint-disable-next-line no-restricted-syntax -- ManageSubscriptionModal: tier-selector cards with active-state border tint and brand pricing CTAs */}
            <button
              onClick={() => setSelectedPeriod('Annual')}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                selectedPeriod === 'Annual'
                  ? 'bg-surface text-ink-900 shadow-sm'
                  : 'text-ink-600 hover:text-ink-900'
              }`}
            >
              Annual
              <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-semibold rounded-full">
                Save 20%
              </span>
            </button>
          </div>
        </div>

        {/* Plan Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {(Object.keys(TIER_INFO) as SubscriptionTier[]).map((tier) => {
            const info = TIER_INFO[tier];
            const isSelected = selectedTier === tier;
            const isCurrent = subscription?.currentTier === tier;
            const isScheduled = subscription?.scheduledTier === tier;
            const tierPrice = selectedPeriod === 'Monthly' ? info.monthlyPrice : info.annualPrice;

            return (
              // eslint-disable-next-line no-restricted-syntax -- ManageSubscriptionModal: tier-selector cards with active-state border tint and brand pricing CTAs
              <button
                key={tier}
                onClick={() => setSelectedTier(tier)}
                // flex-col overrides the UA default that vertically centres a button's
                // content box — without it the shorter cards float their content to the
                // middle of the stretched grid cell and the three headers don't line up.
                className={`relative flex flex-col p-5 border-2 rounded-2xl text-left transition-all ${
                  isSelected
                    ? 'border-blue-400 shadow-lg shadow-blue-100'
                    : 'border-ink-200 hover:border-ink-300 hover:shadow-md'
                }`}
              >
                {(isCurrent || isScheduled) && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className={`px-3 py-1 text-white text-xs font-semibold rounded-full whitespace-nowrap ${
                      isCurrent ? 'bg-blue-600' : 'bg-amber-600'
                    }`}>
                      {isCurrent ? 'Current Plan' : 'Scheduled'}
                    </span>
                  </div>
                )}

                <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${info.gradient} flex items-center justify-center text-white mb-3`}>
                  {info.icon}
                </div>

                <h5 className="text-lg font-bold text-ink-900">{info.name}</h5>
                <p className="text-xs text-ink-500">{info.tagline}</p>
                <div className="mt-1 mb-3">
                  <span className="text-2xl font-bold text-ink-900">${tierPrice}</span>
                  <span className="text-ink-500 text-sm">/mo</span>
                  {selectedPeriod === 'Annual' && (
                    <div className="text-xs text-emerald-600 font-medium mt-0.5">
                      Save ${(info.monthlyPrice - info.annualPrice) * 12}/yr vs monthly
                    </div>
                  )}
                </div>

                <div className="space-y-1.5 flex-1">
                  {info.features.map((feature, index) => {
                    const f = typeof feature === 'string' ? { text: feature } : feature;
                    return (
                      <div key={index} className={`flex items-center gap-1.5 text-xs ${f.badge === 'SOON' ? 'text-ink-400' : 'text-ink-600'}`}>
                        <Check className={`w-3.5 h-3.5 flex-shrink-0 ${f.badge === 'PRO' ? 'text-violet-500' : f.badge === 'SOON' ? 'text-ink-300' : 'text-emerald-500'}`} />
                        <span className="flex-1">{f.text}</span>
                        {f.badge === 'PRO' && <span className="text-[8px] font-extrabold tracking-wider px-1 py-0.5 rounded bg-gradient-to-r from-violet-600 to-purple-500 text-white">PRO</span>}
                        {f.badge === 'SOON' && <span className="text-[8px] font-extrabold tracking-wider px-1 py-0.5 rounded bg-ink-100 text-ink-500 border border-ink-200">SOON</span>}
                      </div>
                    );
                  })}
                </div>
                <p className="mt-2 text-2xs text-ink-400">*$39 per additional seat</p>
              </button>
            );
          })}
        </div>

        {/* Add-ons */}
        <div className="space-y-3">
          <div className="bg-ink-50 rounded-xl p-4 border border-ink-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-semibold text-ink-900">Additional Team Members</span>
                <span className="text-ink-300 text-sm">·</span>
                {/* eslint-disable-next-line no-restricted-syntax -- ManageSubscriptionModal: tier-selector cards with active-state border tint and brand pricing CTAs */}
                <button
                  onClick={() => { onClose(); navigate('/settings', { state: { section: 'team' } }); }}
                  className="text-xs text-blue-600 hover:text-blue-700 font-medium hover:underline transition-colors"
                >
                  Add Members
                </button>
              </div>
              <span className="text-sm font-bold text-ink-900">$39<span className="text-ink-500 font-normal text-xs">/mo per seat</span></span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          {/* eslint-disable-next-line no-restricted-syntax -- ManageSubscriptionModal: tier-selector cards with active-state border tint and brand pricing CTAs */}
          <button
            onClick={onClose}
            className="flex-1 bg-ink-100 text-ink-700 py-2.5 px-4 rounded-xl font-medium hover:bg-ink-200 transition-colors"
          >
            Cancel
          </button>
          {/* eslint-disable-next-line no-restricted-syntax -- ManageSubscriptionModal: tier-selector cards with active-state border tint and brand pricing CTAs */}
          <button
            onClick={handleChangeTier}
            disabled={loading || (selectedTier === subscription?.currentTier && selectedPeriod === subscription?.billingPeriod)}
            className="flex-1 bg-gradient-to-r from-blue-600 to-blue-500 text-white py-2.5 px-4 rounded-xl font-medium hover:shadow-lg hover:shadow-blue-500/25 transition-all disabled:opacity-50 disabled:hover:shadow-none flex items-center justify-center gap-2"
          >
            {loading ? 'Updating...' : (
              <>Switch to {TIER_INFO[selectedTier].name} - ${selectedPeriod === 'Annual' ? `${price * 12}/yr` : `${price}/mo`} <ArrowRight className="w-4 h-4" /></>
            )}
          </button>
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-surface rounded-lg w-full max-w-4xl max-h-[calc(100dvh-2rem)] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-ink-200">
          <div>
            <h2 className="text-xl font-semibold text-ink-900">Manage Subscription</h2>
            <p className="text-sm text-ink-600 mt-1">Update your plan, billing, and subscription settings</p>
          </div>
          {/* eslint-disable-next-line no-restricted-syntax -- ManageSubscriptionModal: tier-selector cards with active-state border tint and brand pricing CTAs */}
          <button
            onClick={onClose}
            className="p-2 hover:bg-ink-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-ink-600" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-ink-200 px-6">
          <div className="flex gap-6">
            {/* eslint-disable-next-line no-restricted-syntax -- ManageSubscriptionModal: tier-selector cards with active-state border tint and brand pricing CTAs */}
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-3 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'overview'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-ink-600 hover:text-ink-900'
              }`}
            >
              Overview
            </button>
            {/* eslint-disable-next-line no-restricted-syntax -- ManageSubscriptionModal: tier-selector cards with active-state border tint and brand pricing CTAs */}
            <button
              onClick={() => setActiveTab('change')}
              className={`py-3 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'change'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-ink-600 hover:text-ink-900'
              }`}
            >
              Change Plan
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading && !subscription ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-12">
              <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
              <p className="text-red-600 text-center">{error}</p>
              {/* eslint-disable-next-line no-restricted-syntax -- ManageSubscriptionModal: tier-selector cards with active-state border tint and brand pricing CTAs */}
              <button
                onClick={loadSubscription}
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Try Again
              </button>
            </div>
          ) : (
            <>
              {activeTab === 'overview' && renderOverview()}
              {activeTab === 'change' && renderChangePlan()}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManageSubscriptionModal;
